import { useQuery } from '@tanstack/react-query'
import { userAPI, examAPI } from '../api/client'
import { useLanguageStore } from '../store/languageStore'
import type { ExamFramework, ExamSkill, ExamSkillPart, ExamType } from '@enghustle/shared'

/**
 * FR-029 — central hook for the user's exam framework.
 * Skill pages call this to render parts/labels/scoring dynamically instead of
 * hardcoding VSTEP structure, so VSTEP and IELTS share the same page code.
 */
export function useExamFramework() {
  const lang = useLanguageStore(s => s.uiLanguage)

  // Shares the 'user-me' cache used by Profile/Onboarding
  const { data: me } = useQuery({ queryKey: ['user-me'], queryFn: userAPI.me, staleTime: 5 * 60 * 1000 })
  const examType: ExamType = ((me as any)?.examType as ExamType) ?? 'vstep'

  const { data: framework, isLoading } = useQuery({
    queryKey: ['exam-framework', examType],
    queryFn: () => examAPI.getFramework(examType),
    staleTime: 10 * 60 * 1000,
  })

  const partLabel = (p: ExamSkillPart): string =>
    (lang === 'en' ? p.labelEn : p.labelVi) || p.labelVi || p.labelEn || p.partCode

  const parts = (skill: ExamSkill): ExamSkillPart[] => framework?.skills?.[skill] ?? []

  const isIelts = examType !== 'vstep'
  const displayName = framework?.displayName ?? (isIelts ? 'IELTS' : 'VSTEP')

  return { examType, framework, isLoading, parts, partLabel, displayName, isIelts }
}
